import styled, { css } from 'styled-components';

import { size } from '../../layout/helpers';
import { ButtonReset } from '../../layout/reset';
import { Wrapper as MediaPreview } from '../MediaPreview/wrapper';

export const TableWrapper = styled.div`
  --_table-tr-border-color: rgba(0, 0, 0, 0.06);
  --_table-tr-hover-bg: rgba(var(--c-overlay-rgb), 0.02);

  isolation: isolate;
`;

export const TableOverflow = styled.div`
  ${(props) => props.theme.breakpoint.Md} {
    overflow: auto;
    margin-inline: calc(var(--main-gutter) * -1);
    padding-inline: var(--main-gutter);
  }
`;

export const Table = styled.div`
  font-size: ${(props) => props.theme.font.size.small};

  ${(props) => props.theme.breakpoint.Md} {
    display: table;
    width: 100%;
    border-collapse: collapse;
  }
`;

export const Thead = styled.div`
  display: table-header-group;

  ${(props) => props.theme.breakpoint.LtMd} {
    display: none;
  }
`;

export const Tbody = styled.div`
  ${(props) => props.theme.breakpoint.Md} {
    display: table-row-group;
  }
`;

interface TrProps {
  $isClickable?: boolean;
  $highlight?: boolean;
}

export const Tr = styled.div<TrProps>`
  position: relative;
  text-decoration: none;
  color: var(--navigation-fg); // Reset color if used as a link

  ${(props) => props.theme.breakpoint.Md} {
    display: table-row;

    ${Tbody} & {
      &:has(:hover) {
        background-color: var(--_table-tr-hover-bg);
      }
    }
  }

  ${(props) => props.theme.breakpoint.LtMd} {
    display: block;
    border: 1px solid var(--c-border-main);
    border-radius: ${size(0.5)};

    & + & {
      margin-top: ${size(1)};
    }
  }

  ${({ $isClickable }) =>
    $isClickable &&
    css`
        cursor: pointer;
      `}
  ${({ $highlight }) =>
    $highlight &&
    css`
        font-weight: bold;
        background-color: var(--_table-tr-hover-bg);
      `}

`;

const cellBaseStyling = css`
  vertical-align: middle;
  white-space: nowrap;

  ${(props) => props.theme.breakpoint.LtMd} {
    white-space: normal;
    text-align: right;
  }

  ${(props) => props.theme.breakpoint.Md} {
    display: table-cell;
  }

  input[type="checkbox"] {
    display: block;
    margin: 0;
    accent-color: var(--c-input-selection-bg-active);
  }
`;

interface CellProps {
  $isMinimal?: boolean;
}

export const Th = styled.div<CellProps>`
  --_th-padding: ${size(2)};

  ${cellBaseStyling};

  padding: var(--_th-padding);
  background-color: var(--c-accent-bg);

  &:first-child {
    border-top-left-radius: ${size(0.5)};
    border-bottom-left-radius: ${size(0.5)};
  }

  &:last-child {
    border-top-right-radius: ${size(0.5)};
    border-bottom-right-radius: ${size(0.5)};
  }

  ${({ $isMinimal }) =>
    $isMinimal &&
    css`
      width: 1px;
    `}
`;

interface TdProps {
  $noShrink?: boolean;
}

export const Td = styled.div<TdProps>`
  ${cellBaseStyling};

  ${(props) => props.theme.breakpoint.LtMd} {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${size(2)};
    padding: ${size(1)};

    &::before {
      content: attr(data-heading);
      flex-shrink: 0;
      font-weight: var(--font-weight-bold);
      font-size: ${(props) => props.theme.font.size.xsmall};
    }

    & + & {
      border-top: 1px solid var(--_table-tr-border-color);
    }
  }

  ${(props) => props.theme.breakpoint.Md} {
    padding: ${size(1.5)} ${size(2)};

    ${({ $noShrink }) =>
      $noShrink &&
      css`
        white-space: nowrap;
      `}

    ${MediaPreview} {
      margin-block: ${size(-0.5)};
    }
  }

  ${Tr}:not(:first-child) & {
    ${(props) => props.theme.breakpoint.Md} {
      border-top: 1px solid var(--_table-tr-border-color);
    }
  }
`;

const ThSortStyling = css`
  display: flex;
  align-items: center;
  gap: ${size(0.75)};
  font-size: ${(props) => props.theme.font.size.xsmall};
`;

export const ThSort = styled(ButtonReset)`
  ${ThSortStyling};
  position: relative;
  width: 100%;
  user-select: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &::before {
    content: "";
    position: absolute;
    inset: calc(var(--_th-padding) * -1);
  }

  svg {
    height: 1em;
    fill: currentColor;
  }
`;

export const ThSortLabel = styled.span`
  ${ThSortStyling};
`;

export const FoldoutButton = styled(ButtonReset)`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--c-accent-bg);
`;

// Header

export const TableHeader = styled.div`
  margin-bottom: ${size(2)};
`;

export const TableHeaderMain = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${size(2)};

  ${(props) => props.theme.breakpoint.LtMd} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const ActiveFilters = styled.div`
  margin-top: ${size(1.5)};
`;

export const TableActions = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: ${size(1)};

  ${(props) => props.theme.breakpoint.LtMd} {
    width: 100%;
  }
`;

export const TableActionsSearch = styled.div`
  ${(props) => props.theme.breakpoint.LtMd} {
    flex: 1 1 auto;
  }
`;

export const TableActionsButtons = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: ${size(0.5)};
`;

export const TableSelection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${size(2)};
  position: sticky;
  top: calc((var(--main-gutter) * -1) + ${size(1)});
  margin-bottom: ${size(1)};
  padding: ${size(1.5)};
  background-color: var(--c-bg-main);
  border: 1px solid var(--c-border-main);
  border-radius: ${size(0.5)};
  z-index: 10;
`;

export const TableSelectionMain = styled.div`
  display: flex;
  align-items: center;
  gap: ${size(1)};
`;

export const TableSelectionCancelButton = styled(ButtonReset)`
  --_size: ${size(4)};

  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--_size);
  height: var(--_size);
  border-radius: 50%;
  transform-origin: 50% 50%;
  transition: transform 200ms ease;
  user-select: none;
  cursor: pointer;

  &:enabled {
    &:active {
      transform: scale(0.93);
    }

    &:hover {
      background-color: var(--c-semantic-neutral-300);
    }
  }
`;
